<template>
    <b-container fluid="true">
        <b-row no-gutters class="py-2 border-light-grey">
            {{ /* picker */ }}
            <b-col
                cols="12" sm="4" class="modal-small-text text-center mb-2 mb-sm-0">
                <div>{{ $t('sensors-page.camera-modal.show-images-from') }}:</div>
                    <div>
                        <div class="d-inline">
                            <BIcon icon="calendar3" class="i-purple-color"></BIcon>
                        </div>
                        <VueCtkDateTimePicker
                            v-model="date"
                            button-color="#6299EB"
                            color="#6299EB"
                            format="YYYY-MM-DD"
                            formatted="l"
                            label=""
                            :locale="$i18n.locale"
                            :range="false"
                            input-size="sm"
                            :no-header="true"
                            :no-shortcuts="true"
                            output-format="YYYY-MM-DD"
                            :only-date="true"
                            :right="false"
                            id="date-time-picker-for-camera"
                            class="datetime-pk d-inline"
                            :no-button-now="true"
                            @is-hidden="fetchCameraImages"
                        >
                            <b-button variant="link" class="datetime-pk-trigger"></b-button>
                        </VueCtkDateTimePicker>
                    </div>
            </b-col>

            {{ /* or */ }}
            <b-col
                cols="12" sm="4"
                class="modal-small-grey-text d-flex justify-content-center align-items-center mb-2 mb-sm-0"
            >{{ $t('sensors-page.camera-modal.or') }}</b-col>

            {{ /* new image button */ }}
            <b-col
                cols="12" sm="4"
                class="modal-small-text d-flex flex-column justify-content-center align-items-center mb-2 mb-sm-0">
                <div class="modal-small-text text-center">
                    {{ $t('sensors-page.camera-modal.shoot-now') }}
                </div>
                <div>
                    <WhiteIconButton
                        icon="camera"
                        :text="$t('sensors-page.camera-modal.shoot')"
                        :hide-text-mobile="false"
                        :emit-this="'fetch-take-pictures'"
                        :disabled="loading || imageCommand.disableBtn"
                        v-on:fetch-take-pictures="takeNewPicture"
                    ></WhiteIconButton>
                </div>
            </b-col>
        </b-row>

        {{ /* carousel, error, no data or loading overlay */ }}
        <b-overlay :show="loading" rounded="sm">
            <b-row no-gutters v-if="imagesList.length > 0" :key="forceKey">
                <b-col cols="12" class="mx-auto py-3 py-sm-4">
                    <CarouselGalleryWrapper :images="imagesList"></CarouselGalleryWrapper>
                </b-col>
            </b-row>
            <b-row no-gutters v-else-if="imageCommand.showMessage">
                <b-col cols="12" class="mx-auto py-3 py-sm-4">
<!--                    <b-img-->
<!--                        :src="imagesList[0]"-->
<!--                        :fluid="true"-->
<!--                        :alt="$t('sensors-page.camera-modal.dam-photo')"-->
<!--                    ></b-img>-->
                    <div class="py-3 text-center modal-text-message">
                        <span v-if="imageCommand.success">
                            {{ $t('sensors-page.camera-modal.new-image-taken') }}
                        </span>
                        <span class="text-danger" v-else-if="!imageCommand.success">
                            {{ $t('sensors-page.camera-modal.new-image-taken-fail') }}
                        </span>
                    </div>
                </b-col>
            </b-row>
            <!-- General error -->
            <b-row no-gutters v-else-if="error">
                <b-col cols="12" class="text-center modal-text-message text-danger py-4">
                    {{ $t('sensors-page.camera-modal.error-message') }}
                </b-col>
            </b-row>
            <b-row no-gutters v-else class="py-3">
                <b-col cols="12" class="text-center modal-text-message">
                    {{ $t('sensors-page.camera-modal.no-results-message') }}
                </b-col>
                <b-col cols="12" class="text-center modal-text-message">
                    {{ $t('sensors-page.camera-modal.please-select') }}
                </b-col>
            </b-row>
        </b-overlay>
    </b-container>
</template>

<script>
    // utils
    import {formatShortDate} from "../../utils/common";
    import apiCall from "../../utils/api";
    // vuex
    import {mapGetters, mapActions} from "vuex";
    import {AUTH_LOGOUT} from "../../store/actions/auth";
    // external
    import {BIcon} from 'bootstrap-vue';
    import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
    import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';
    // components
    import WhiteIconButton from "../shared/WhiteIconButton";
    import CarouselGalleryWrapper from "../shared/CarouselGalleryWrapper";

    /**
     * Body of the modal. The real content. Holds a
     * date picker, button and the gallery wrapper.
     * Has methods for getting data and it shows
     * different components depending on status.
     */

    export default {
        name: "CameraModalBody",
        components: {
            BIcon,
            VueCtkDateTimePicker,
            WhiteIconButton,
            CarouselGalleryWrapper
        },
        data() {
            return {
                date: formatShortDate(),
                data: {
                    folder: "",
                    images: []
                },
                imageCommand: {
                    success: false,
                    showMessage: false,
                    disableBtn: false
                },
                loading: false,
                error: false,
                forceKey: 0
            }
        },
        computed: {
            ...mapGetters(['currentDamSelectedId']),
            /**
             * Build urls for each image found in data. Item at index 0 is the folder!
             * If direct links are provided, use them directly, otherwise build path to file.
             * NOTE: in production no links should be passed, but it is still a good option.
             * @returns {[string,]}
             */
            imagesList() {
                let imagesList = [];

                if (this.data["images"].length > 0) {
                    const folder = this.data["folder"];

                    for (let index = 0; index < this.data["images"].length; index++) {
                        imagesList.push(`/media/${folder}/${this.data["images"][index]}`);
                    }
                }

                return imagesList;
            }
        },
        methods: {
            ...mapActions([AUTH_LOGOUT]),
            /**
             * Taking a new image is a MQTT command.
             * Params are a subset of the old params used.
             * @returns {Promise<void>}
             */
            async takeNewPicture() {
               const params = {
                    "ps_name_index": this.currentDamSelectedId
                };

                this.loading = true;
                this.error = false;

                try {
                    const response = await apiCall.shootNewImage(params);

                    this.imageCommand.success = response.data.result;
                    this.imageCommand.showMessage = true;
                    this.imageCommand.disableBtn = true;
                    this.loading = false;
                }
                catch (error) {
                    this.loading = false;
                    this.error = true;
                    console.log(error);

                    if (error.request.status === 401) {
                        // if resp is unauthorized, logout
                        this.AUTH_LOGOUT();
                    }
                }
            },
            /**
             * Get camera images.
             * Params are a subset of the old params used.
             * @returns {Promise<void>}
             */
            async fetchCameraImages() {
               const params = {
                    "select_day": this.date,
                    "ps_name_index": this.currentDamSelectedId
                };

                this.loading = true;
                this.error = false;

                try {
                    const response = await apiCall.getCameraImages(params);
                    this.data = response.data.result;
                    this.loading = false;
                }
                catch (error) {
                    this.loading = false;
                    this.error = true;
                    this.data.folder = '';
                    this.data.images = [];
                    console.log(error);
                    if (error.request.status === 401) {
                        // if resp is unauthorized, logout
                        this.AUTH_LOGOUT();
                    }
                }
            }
        },
      mounted() {
          this.fetchCameraImages();
      },
      watch: {
            imagesList() {
                // force update the gallery wrapper
                this.forceKey++;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../assets/scss/variables";

    .border-light-grey {
        border-bottom: 1px solid $lightGrey;
    }

    .modal-small-text {
        text-align: left;
        font: normal normal normal 14px/19px Nunito, sans-serif;
        letter-spacing: 0px;
        color: $darkColor;
        opacity: 1;
    }
    .modal-small-grey-text {
        text-align: left;
        font: normal normal normal 12px/16px Nunito, sans-serif;
        letter-spacing: 0;
        color: $greyColor;
        opacity: 1;
    }

    .modal-text-message {
        text-align: center;
        font: normal normal normal 14px/19px Nunito, sans-serif;
        letter-spacing: 0;
        color: $mediumLightGrey;
        opacity: 1;
    }

    .i-purple-color {
        color: $purpleColor;
    }

    #datepicker-buttons-container {
        width: 100%;
    }

    .datetime-pk {
        .datetime-pk-trigger {
            color: $darkColor;
            font: normal normal 500 14px/19px Nunito, sans-serif;
            letter-spacing: 0;
            opacity: 1;
            outline: none;
            &:focus {
                box-shadow: none !important;
            }
            &hover {
                color: $menuBlue;
                text-decoration: none;
            }
        }
    }
</style>